import { useEffect, useRef } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { toast } from 'sonner';
import { useCanWrite } from './useCanWrite';

interface ProtectedWriteRouteProps {
  readonly children?: React.ReactNode;
}

/**
 * Route guard for pages that exist only to create or change business data.
 *
 * A view-only user is redirected to `/` with a toast, instead of filling in a form the backend
 * answers `403` to on submit. Every other role passes through, including while the user context is
 * still loading — see `useCanWrite` for why this fails open.
 */
export function ProtectedWriteRoute({ children }: ProtectedWriteRouteProps) {
  const { canWrite, isLoading } = useCanWrite();
  const toastShownRef = useRef(false);

  useEffect(() => {
    if (!isLoading && !canWrite && !toastShownRef.current) {
      toastShownRef.current = true;
      toast.error('Your access is view-only, so this page is not available');
    }
  }, [isLoading, canWrite]);

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }

  if (!canWrite) {
    return <Navigate to="/" replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
